<script setup>
import { Link, usePage, useRemember } from '@inertiajs/vue3';
import {
    ChevronDown,
    ChevronRight,
    House,
    Sliders,
    FileSignature,
    UsersRound,
    Contact,
    Leaf,
    ShoppingCart,
    Building2,
    Factory,
    BarChart3,
    RefreshCw,
    HardHat,
    Layers,
    Link2,
    Link as LinkIcon
} from '@lucide/vue';
import { computed, reactive, watchEffect, getCurrentInstance, markRaw, ref } from 'vue';
import ContractSelectionModal from '@/components/Dashboard/ContractSelectionModal.vue';
import ContractAwardSelectionModal from '@/components/Dashboard/ContractAwardSelectionModal.vue';
import SocialReturnAgreementsContractSelectionModal from '@/components/Dashboard/SocialReturnAgreementsContractSelectionModal.vue';
import ZeroEmissionAgreementsContractSelectionModal from '@/components/Dashboard/ZeroEmissionAgreementsContractSelectionModal.vue';
import CirculairAgreementsContractSelectionModal from '@/components/Dashboard/CirculairAgreementsContractSelectionModal.vue';
import ZeroEmissionDocumentContractSelectionModal from '@/components/Dashboard/ZeroEmissionDocumentContractSelectionModal.vue';
import ContractorSelectionModal from '@/components/Dashboard/ContractorSelectionModal.vue';
import ContractorZeMaterialsSelectionModal from '@/components/Dashboard/ContractorZeMaterialsSelectionModal.vue';
import ContractClientSelectionModal from '@/components/Dashboard/ContractClientSelectionModal.vue';

const props = defineProps({
    collapsed: { type: Boolean, default: false },
});

// Access global properties for route function in SSR/CSR
const instance = getCurrentInstance();
const route = instance?.appContext.config.globalProperties.route;

const page = usePage();
const menuItems = computed(() => page.props.SidebarMenuItems || {});
const currentRouteName = computed(() => page.props.currentRouteName || '');

// Freeze displayed menu items when navigating to routes not present in menu
const displayMenuItemsState = useRemember({ items: {} }, 'SidebarMenuItemsDisplay');
const displayMenuItems = computed(() => displayMenuItemsState.value.items || {});

const iconMap = {
    // Core/legacy keys
    WIZZR_ICON_Home: House,
    WIZZR_ICON_Instellingen: Sliders,
    WIZZR_ICON_Contract: FileSignature,
    WIZZR_ICON_Personen: Contact,
    WIZZR_sociaal_inkopen: ShoppingCart,
    WIZZR_mvo_sr_projecten: Leaf,

    // New specific keys for clearer icons per section
    WIZZR_ICON_Clients: Building2,
    WIZZR_ICON_Contractors: Factory,
    WIZZR_ICON_Kandidaten: UsersRound,
    WIZZR_ICON_Rapportages: BarChart3,
    WIZZR_zero_emissie: Leaf,
    WIZZR_circulair: RefreshCw,
    WIZZR_ICON_Onderaannemer: HardHat,
    WIZZR_ICON_ketenmodule: Layers,
    WIZZR_ICON_Keten: Link2,
    WIZZR_ICON_Ketenapraken: LinkIcon,
    WIZZR_ICON_Link: Link2,
};

const getIconComponent = (iconName) => {
    if (!iconName) return null;
    return iconMap[iconName] || null;
};

// Build a stable id for sidebar links based on the route name (module)
// Falls back to the menu key when no route is present
const buildSidebarLinkId = (routeOrKey) => {
    if (!routeOrKey) return null;
    const normalized = String(routeOrKey)
        .toLowerCase()
        .replace(/\./g, '-')
        .replace(/[^a-z0-9-_]/g, '');
    return `sidebar-link-${normalized}`;
};

// Preserve sidebar state across Inertia navigations so it doesn't collapse
// on pages that don't have a corresponding menu item.
const sidebarState = useRemember(
    {
        openSubmenus: {},
        activeItems: {},
    },
    'SidebarMenu',
);

// Ensure nested objects are reactive at runtime and persisted in remembered ref
const openSubmenus = reactive(sidebarState.value.openSubmenus || {});
const activeItems = reactive(sidebarState.value.activeItems || {});
sidebarState.value.openSubmenus = openSubmenus;
sidebarState.value.activeItems = activeItems;

// Check if a menu item route matches the current route
const isRouteActive = (menuRoute) => {
    if (!menuRoute || menuRoute === 'javascript:') return false;

    const currentRoute = currentRouteName.value;
    if (!currentRoute) return false;

    // Direct match
    if (currentRoute === menuRoute) {
        return true;
    }

    return false;
};

// Recursively check if any item in the menu is active
const hasActiveItem = (items) => {
    if (!items) return false;
    for (const key in items) {
        const item = items[key];
        if (item.route && isRouteActive(item.route)) {
            return true;
        }
        if (item.submenu) {
            if (hasActiveItem(item.submenu)) {
                return true;
            }
        }
    }
    return false;
};

// Recursively find active items and their parents
const findActiveItems = (items, parentKeys = []) => {
    if (!items) return;

    for (const key in items) {
        const item = items[key];

        // Check if this item is active
        if (item.route && isRouteActive(item.route)) {
            // Mark this specific item as active using its route as the key
            activeItems[item.route] = true;
            // Also mark it with its menu key for template usage
            activeItems[key] = true;

            // Open all parent menus only when not collapsed
            if (!props.collapsed) {
                parentKeys.forEach((parentKey) => {
                    openSubmenus[parentKey] = true;
                });
            }
        }

        // Recursively check submenu items
        if (item.submenu) {
            findActiveItems(item.submenu, [...parentKeys, key]);
        }
    }
};

const initializeMenuState = (items) => {
    if (!items) return;

    // Reset states
    Object.keys(openSubmenus).forEach((key) => {
        openSubmenus[key] = false;
    });
    Object.keys(activeItems).forEach((key) => {
        activeItems[key] = false;
    });

    // Initialize submenu tracking
    for (const key in items) {
        const item = items[key];
        if (item?.submenu) {
            openSubmenus[key] = false;
            initializeMenuState(item.submenu);
        }
    }

    // Find and set active items
    findActiveItems(items);
};

watchEffect(() => {
    const incomingItems = menuItems.value;

    // First-time setup: if we have no display items yet, adopt the current menu
    if (!displayMenuItemsState.value.items || Object.keys(displayMenuItemsState.value.items).length === 0) {
        displayMenuItemsState.value.items = incomingItems;
        initializeMenuState(incomingItems);
        return;
    }

    // Only update the displayed menu and state when the new route is present in the menu
    if (hasActiveItem(incomingItems)) {
        displayMenuItemsState.value.items = incomingItems;
        initializeMenuState(incomingItems);
    }
    // Otherwise, do nothing: keep currently displayed items and open/active state
});

const toggleSubmenu = (key) => {
    if (!(key in openSubmenus)) {
        openSubmenus[key] = false;
    }
    openSubmenus[key] = !openSubmenus[key];
};

const collapseAllSubmenus = () => {
    for (const key in openSubmenus) {
        openSubmenus[key] = false;
    }
};

// ============================================================================
// MODAL SYSTEM - Central registry for sidebar menu modals
// ============================================================================
// To add a new modal from sidebar:
// 1. Import the modal component above
// 2. Add it to MODAL_REGISTRY with a unique key
// 3. Add 'has_modal' and 'modal_component' to menu item in SidebarMenuItems.php

const MODAL_REGISTRY = {
    ContractSelectionModal: markRaw(ContractSelectionModal),
    ContractAwardSelectionModal: markRaw(ContractAwardSelectionModal),
    SocialReturnAgreementsContractSelectionModal: markRaw(SocialReturnAgreementsContractSelectionModal),
    ZeroEmissionAgreementsContractSelectionModal: markRaw(ZeroEmissionAgreementsContractSelectionModal),
    CirculairAgreementsContractSelectionModal: markRaw(CirculairAgreementsContractSelectionModal),
    ZeroEmissionDocumentContractSelectionModal: markRaw(ZeroEmissionDocumentContractSelectionModal),
    ContractorSelectionModal: markRaw(ContractorSelectionModal),
    ContractorZeMaterialsSelectionModal: markRaw(ContractorZeMaterialsSelectionModal),
    ContractClientSelectionModal: markRaw(ContractClientSelectionModal),
    // Future modals add here:
    // 'FeatureModal': markRaw(FeatureModal),
    // 'SettingsModal': markRaw(SettingsModal),
};

// Modal state management
const activeModal = ref(null);
const activeModalComponent = ref(null);

// Handle menu item click - check for modal or navigation
const handleMenuClick = (item, event) => {
    // If item has modal configuration, open modal instead of navigating
    if (item.has_modal && item.modal_component) {
        // Validate configuration before opening
        if (validateModalConfig(item)) {
            openMenuModal(item);
            event.preventDefault();
            return false;
        }
    }

    // Default navigation behavior - let Link handle it
    return true;
};

// Open modal for menu item with modal_component
const openMenuModal = (item) => {
    const componentName = item.modal_component;
    const component = MODAL_REGISTRY[componentName];

    if (component) {
        activeModalComponent.value = component;
        activeModal.value = item.key || componentName;
    } else {
        console.warn(`Modal component "${componentName}" not found in MODAL_REGISTRY. Available:`, Object.keys(MODAL_REGISTRY));
    }
};

// Close modal
const closeModal = () => {
    activeModal.value = null;
    activeModalComponent.value = null;
};

// Handle modal selection
const handleModalSelect = (data) => {
    // Modal will handle its own navigation
    closeModal();
};

// Validate modal configuration (helps catch typos during development)
const validateModalConfig = (item) => {
    if (item.has_modal && item.modal_component) {
        if (!MODAL_REGISTRY[item.modal_component]) {
            console.error(`Invalid modal_component "${item.modal_component}" for item "${item.title}". Available:`, Object.keys(MODAL_REGISTRY));
            return false;
        }
    }
    return true;
};

defineExpose({
    collapseAllSubmenus,
});
</script>

<template>
    <nav class="sidebar">
        <ul class="sidebar-menu">
            <li v-for="(item, key) in displayMenuItems" :key="key" class="menu-item" :class="item.theme ? { [item.theme]: true } : {}">
                <template v-if="!item.submenu">
                    <Link
                        v-if="item.route && item.route !== 'javascript:'"
                        :href="route(item.route, item.params || {})"
                        :id="buildSidebarLinkId(item.route || key)"
                        class="menu-link"
                        :class="{ active: activeItems[key] || activeItems[item.route] }"
                    >
                        <component :is="getIconComponent(item.icon)" v-if="item.icon" />
                        <span>{{ item.title }}</span>
                        <ChevronRight v-if="activeItems[key] || activeItems[item.route]" class="active-indicator" />
                    </Link>
                    <a v-else href="#" :id="buildSidebarLinkId(item.route || key)" class="menu-link" @click.prevent>
                        <component :is="getIconComponent(item.icon)" v-if="item.icon" />
                        <span>{{ item.title }}</span>
                    </a>
                </template>
                <template v-else>
                    <template v-if="props.collapsed">
                        <template v-if="item.route && item.route !== 'javascript:'">
                            <Link :href="route(item.route, item.params || {})" :id="buildSidebarLinkId(item.route || key)" class="menu-link">
                                <component :is="getIconComponent(item.icon)" v-if="item.icon" />
                                <span>{{ item.title }}</span>
                            </Link>
                        </template>
                        <template v-else>
                            <div class="menu-link" :id="buildSidebarLinkId(item.route || key)" @click.stop>
                                <component :is="getIconComponent(item.icon)" v-if="item.icon" />
                                <span>{{ item.title }}</span>
                            </div>
                        </template>
                    </template>
                    <template v-else>
                        <div class="toggle-menu" :class="{ active: openSubmenus[key] }" @click="toggleSubmenu(key)">
                            <div class="toggle-menu-left">
                                <component :is="getIconComponent(item.icon)" v-if="item.icon" />
                                <span>{{ item.title }}</span>
                            </div>
                            <ChevronDown class="chevron" />
                        </div>
                    </template>
                    <ul class="submenu" :class="{ active: openSubmenus[key] }">
                        <li v-for="(subItem, subKey) in item.submenu" :key="subKey">
                            <template v-if="subItem.submenu">
                                <template v-if="props.collapsed">
                                    <template v-if="subItem.route && subItem.route !== 'javascript:'">
                                        <Link
                                            :href="route(subItem.route, subItem.params || {})"
                                            :id="buildSidebarLinkId(subItem.route || subKey)"
                                            class="menu-link"
                                        >
                                            <span>{{ subItem.title }}</span>
                                        </Link>
                                    </template>
                                    <template v-else>
                                        <div class="menu-link" :id="buildSidebarLinkId(subItem.route || subKey)" @click.stop>
                                            <span>{{ subItem.title }}</span>
                                        </div>
                                    </template>
                                </template>
                                <template v-else>
                                    <div class="toggle-menu sub-toggle" :class="{ active: openSubmenus[subKey] }" @click.stop="toggleSubmenu(subKey)">
                                        <span>{{ subItem.title }}</span>
                                        <ChevronDown class="chevron sub-chevron" />
                                    </div>
                                </template>
                                <ul class="submenu nested-submenu" :class="{ active: openSubmenus[subKey] }">
                                    <li v-for="(nestedItem, nestedKey) in subItem.submenu" :key="nestedKey">
                                        <Link
                                            v-if="nestedItem.route && nestedItem.route !== 'javascript:'"
                                            :href="route(nestedItem.route, nestedItem.params || {})"
                                            :id="buildSidebarLinkId(nestedItem.route || nestedKey)"
                                            class="menu-link"
                                            :class="{ active: activeItems[nestedKey] || activeItems[nestedItem.route] }"
                                        >
                                            {{ nestedItem.title }}
                                            <ChevronRight v-if="activeItems[nestedKey] || activeItems[nestedItem.route]" class="active-indicator" />
                                        </Link>
                                        <a v-else href="#" :id="buildSidebarLinkId(nestedItem.route || nestedKey)" class="menu-link" @click.prevent>{{
                                            nestedItem.title
                                        }}</a>
                                    </li>
                                </ul>
                            </template>
                            <template v-else>
                                <Link
                                    v-if="subItem.route && subItem.route !== 'javascript:' && !subItem.has_modal"
                                    :href="route(subItem.route, subItem.params || {})"
                                    :id="buildSidebarLinkId(subItem.route || subKey)"
                                    class="menu-link"
                                    :class="{ active: activeItems[subKey] || activeItems[subItem.route] }"
                                >
                                    {{ subItem.title }}
                                    <ChevronRight v-if="activeItems[subKey] || activeItems[subItem.route]" class="active-indicator" />
                                </Link>
                                <a
                                    v-else-if="subItem.has_modal && subItem.modal_component"
                                    href="#"
                                    :id="buildSidebarLinkId(subItem.route || subKey)"
                                    class="menu-link"
                                    :class="{ active: activeItems[subKey] || activeItems[subItem.route] }"
                                    @click="handleMenuClick(subItem, $event)"
                                >
                                    {{ subItem.title }}
                                    <ChevronRight v-if="activeItems[subKey] || activeItems[subItem.route]" class="active-indicator" />
                                </a>
                                <a v-else href="#" :id="buildSidebarLinkId(subItem.route || subKey)" class="menu-link" @click.prevent>
                                    {{ subItem.title }}
                                </a>
                            </template>
                        </li>
                    </ul>
                </template>
            </li>
        </ul>
    </nav>

    <!-- Modal for menu items with modal_component -->
    <component v-if="activeModalComponent" :is="activeModalComponent" v-model="activeModal" @close="closeModal" @select="handleModalSelect" />
</template>

<style scoped>
#sidebar-link-chain-module-imposed-targets-index {
    font-size: 14px;
}
</style>
